// Legacy interface for backward compatibility with existing components
export interface RewardHistoryItem {
  id: string;
  user: {
    name: string;
    email: string;
    avatar?: string;
  };
  reward: {
    name: string;
    image?: string;
  };
  dateRedeemed: string;
  pointsUsed: number;
  status: "Redeemed" | "Sent" | "Pending" | "Failed";
  actions: {
    canResend: boolean;
    lastResent?: string;
  };
}

export interface RewardHistoryAPIItem {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_avatar?: string;
  coupon_id: string;
  coupon_name: string;
  coupon_code: string;
  points_used: number;
  exchange_date: string;
  redemption_status: "redeemed" | "used" | "pending" | "failed";
  can_resend: boolean;
  resend_count: number;
  redeemed_date?: string;
  used_date?: string;
  resend_date?: string;
}

export interface RewardHistoryFilter {
  search: string;
  userSearch: string;
  rewardSearch: string;
  status: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface RewardHistoryPagination {
  page: number;
  size: number;
  total: number;
}

// Helper function to format date to client timezone with AM/PM
const formatDateToClientTimezone = (dateString: string): string => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    // Format time in 12-hour format with AM/PM
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, "0");

    return `${month}/${day}/${year} ${formattedHours}:${minutes} ${ampm}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString; // Return original if formatting fails
  }
};

// Helper function to transform API data to legacy format
export const transformAPIItemToLegacy = (apiItem: RewardHistoryAPIItem): RewardHistoryItem => {
  return {
    id: apiItem.id,
    user: {
      name: apiItem.user_name || "",
      email: apiItem.user_email || "",
      avatar: apiItem.user_avatar,
    },
    reward: {
      name: apiItem.coupon_name || "",
      image: undefined, // API doesn't provide image
    },
    dateRedeemed: formatDateToClientTimezone(apiItem.exchange_date),
    pointsUsed: apiItem.points_used || 0,
    status: mapAPIStatusToLegacy(apiItem.redemption_status),
    actions: {
      canResend: apiItem.can_resend,
      lastResent: apiItem.resend_date ? formatDateToClientTimezone(apiItem.resend_date) : undefined,
    },
  };
};

// Helper function to map API status to legacy status
const mapAPIStatusToLegacy = (
  apiStatus: RewardHistoryAPIItem["redemption_status"],
): "Redeemed" | "Sent" | "Pending" | "Failed" => {
  switch (apiStatus) {
    case "redeemed":
      return "Redeemed";
    case "used":
      return "Sent";
    case "pending":
      return "Pending";
    default:
      return "Failed";
  }
};
